import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PurchaseForm from "./Purchaseform";
import { useTranslation } from "react-i18next";
import {
  X,
  Hash,
  DollarSign,
  Building2,
  FileText,
  Copy,
  Check,
} from "lucide-react";

export default function ProductdetailModal({
  product,
  onClose,
  isAuthenticated = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language.startsWith("ar");
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(false);

const quantityTypes = Array.isArray(product?.quantity_types)
  ? product.quantity_types
  : [];
const quantityType = product?.quantity_type;

const unitPrice = Number(quantityType?.cost_per_kilo);
const packageSize = Number(quantityType?.size);

const totalPrice =
  quantityType &&
  quantityType.cost_per_kilo != null &&
  quantityType.size != null &&
  Number.isFinite(unitPrice) &&
  Number.isFinite(packageSize)
    ? unitPrice * packageSize
    : null;

  const handleCopy = () => {
    navigator.clipboard?.writeText(product.cas_number || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const handlePurchaseClick = () => {
    if (!isAuthenticated) {
      onClose();
      navigate("/login");
    } else {
      setShowForm(true);
    }
  };

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: "0",
        background: "rgba(0,0,0,0.45)",
        zIndex: "1000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <div
        dir={isRtl ? "rtl" : "ltr"}
        style={{
          background: "#ffffff",
          borderRadius: "24px",
          width: "100%",
          maxWidth: "520px",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
          textAlign: isRtl ? "right" : "left",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            [isRtl ? "left" : "right"]: "14px",
            zIndex: "10",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "1px solid #e5e7eb",
            background: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <X size={16} />
        </button>

        <div
          style={{
            height: "180px",
            background: "rgba(12,104,57,0.06)",
            borderRadius: "24px 24px 0 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              style={{
                maxHeight: "160px",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <svg
              width="56"
              height="56"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(12,104,57,0.3)"
              strokeWidth="1.2"
            >
              <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
            </svg>
          )}
        </div>

        <div style={{ padding: "28px", boxSizing: "border-box" }}>
          {!showForm ? (
            <>
              <h2
                style={{
                  fontSize: "22px",
                  fontWeight: "700",
                  color: "#1a1f1c",
                  marginBottom: "10px",
                  marginTop: "0",
                }}
              >
                {product.name}
              </h2>

              <span
                style={{
                  display: "inline-block",
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#0c6839",
                  background: "rgba(12,104,57,0.1)",
                  border: "1px solid rgba(12,104,57,0.2)",
                  padding: "5px 14px",
                  borderRadius: "20px",
                  marginBottom: "22px",
                }}
              >
                {product.family?.name ||
                  product.category?.name ||
                  t("products.chemicals")}
              </span>

              <div style={{ marginBottom: "20px" }}>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#1a1f1c",
                    marginBottom: "8px",
                    marginTop: "0",
                  }}
                >
                  <FileText size={15} color="#0c6839" />
                  {t("products.description")}
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    lineHeight: "1.7",
                    margin: "0",
                  }}
                >
                  {product.description || t("products.noDescription")}
                </p>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "#f8faf8",
                    borderRadius: "12px",
                    padding: "13px 16px",
                  }}
                >
                  <span
                    style={{
                      width: "34px",
                      height: "34px",
                      background: "rgba(12,104,57,0.1)",
                      borderRadius: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0c6839",
                      flexShrink: "0",
                    }}
                  >
                    <Hash size={16} />
                  </span>
                  <div
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {t("products.casNumber")}
                    </span>
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1a1f1c",
                      }}
                    >
                      {product.cas_number || "N/A"}
                    </span>
                  </div>
                  <button
                    onClick={handleCopy}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: copied ? "#0c6839" : "#9ca3af",
                    }}
                  >
                    {copied ? <Check size={15} /> : <Copy size={15} />}
                  </button>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "#f8faf8",
                    borderRadius: "12px",
                    padding: "13px 16px",
                  }}
                >
                  <span
                    style={{
                      width: "34px",
                      height: "34px",
                      background: "rgba(12,104,57,0.1)",
                      borderRadius: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0c6839",
                      flexShrink: "0",
                    }}
                  >
                    <DollarSign size={16} />
                  </span>
                  <div
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {t("products.totalPrice")}
                    </span>
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1a1f1c",
                      }}
                    >
                      {totalPrice !== null
                        ? `${t("products.currency")} ${totalPrice.toLocaleString()}`
                        : t("products.contactForPrice")}
                    </span>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "#f8faf8",
                    borderRadius: "12px",
                    padding: "13px 16px",
                  }}
                >
                  <span
                    style={{
                      width: "34px",
                      height: "34px",
                      background: "rgba(12,104,57,0.1)",
                      borderRadius: "9px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#0c6839",
                      flexShrink: "0",
                    }}
                  >
                    <Building2 size={16} />
                  </span>
                  <div
                    style={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <span style={{ fontSize: "11px", color: "#9ca3af" }}>
                      {t("products.source")}
                    </span>
                    <span
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1a1f1c",
                      }}
                    >
                      {product.supplier?.name ||
                        product.origin ||
                        t("products.international")}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePurchaseClick}
                style={{
                  width: "100%",
                  padding: "15px",
                  background: "#0c6839",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  cursor: "pointer",
                }}
              >
                {t("products.purchaseRequest")}
              </button>
            </>
          ) : (
            <PurchaseForm
              product={product}
              onBack={() => setShowForm(false)}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
